import {
  IconBuildingFactory2,
  IconCloud,
  IconClockHour4,
  IconArrowsRightLeft,
  IconMail,
  IconUserPlus,
  IconShieldCheck,
  IconKey,
  IconLock,
  IconTags,
  IconRefresh,
  type IconProps,
} from '@tabler/icons-react'
import type { ComponentType } from 'react'

export type StepCategory = 'setup' | 'environment' | 'sync'

export const ACTIVE_JOB_STATUSES = ['pending', 'running'] as const
export const TERMINAL_JOB_STATUSES = new Set(['completed', 'failed', 'cancelled'])

interface StepMeta {
  label: string
  icon: ComponentType<IconProps>
  describe: (input: Record<string, unknown>) => string
}

const STEP_META: Record<string, StepMeta> = {
  create_ou: {
    label: 'Criar OU',
    icon: IconBuildingFactory2,
    describe: (i) => (typeof i.name === 'string' ? `Nome: ${i.name}` : 'Organizational Unit'),
  },
  create_account: {
    label: 'Criar conta AWS',
    icon: IconCloud,
    describe: (i) => (typeof i.account_name === 'string' ? i.account_name : 'Nova conta'),
  },
  wait_account_active: {
    label: 'Aguardar conta ATIVA',
    icon: IconClockHour4,
    describe: (i) => `Polling DescribeCreateAccountStatus · timeout ${i.timeout_seconds ?? 300}s`,
  },
  move_account: {
    label: 'Mover conta para sub-OU',
    icon: IconArrowsRightLeft,
    describe: () => 'Root → sub-OU de ambiente',
  },
  create_email_mailbox: {
    label: 'Criar mailbox no WorkMail',
    icon: IconMail,
    describe: (i) => (typeof i.email === 'string' ? i.email : ''),
  },
  create_iam_user: {
    label: 'Criar IAM user',
    icon: IconUserPlus,
    describe: (i) => `Username: ${i.user_name ?? 'AUTOMATION'}`,
  },
  attach_iam_policy: {
    label: 'Anexar IAM policy',
    icon: IconShieldCheck,
    describe: (i) => {
      if (typeof i.policy_arn !== 'string') return 'AdministratorAccess'
      return i.policy_arn.split('/').pop() ?? i.policy_arn
    },
  },
  create_access_key: {
    label: 'Gerar access key',
    icon: IconKey,
    describe: (i) => `Para o user ${i.user_name ?? 'AUTOMATION'}`,
  },
  store_secret: {
    label: 'Persistir credenciais no Secrets Manager',
    icon: IconLock,
    describe: (i) => (typeof i.secret_name === 'string' ? i.secret_name : ''),
  },
  apply_tags_account: {
    label: 'Aplicar tags na conta AWS',
    icon: IconTags,
    describe: (i) => `${countTags(i.tags)} tags`,
  },
  apply_tags_ou: {
    label: 'Aplicar tags na OU',
    icon: IconTags,
    describe: (i) => `${countTags(i.tags)} tags`,
  },
  apply_tags_secret: {
    label: 'Aplicar tags no secret',
    icon: IconTags,
    describe: (i) => `${countTags(i.tags)} tags`,
  },
  // Role de leitura do ConsoleAI na conta nova (consoleai#25003).
  ensure_read_role: {
    label: 'Acesso de leitura do ConsoleAI',
    icon: IconShieldCheck,
    describe: () => 'Role de leitura na conta nova, conferida com acesso de verdade',
  },
  // Roles do runner de Terraform na conta nova (consoleai#25870).
  ensure_runner_roles: {
    label: 'Acesso do @Atendente para criar ambiente',
    icon: IconShieldCheck,
    describe: () => 'Roles de plan e apply do runner na conta nova, conferidas na conta',
  },
  force_org_sync: {
    label: 'Sincronizar AWS Organizations → DB',
    icon: IconRefresh,
    describe: () => 'Reflete estrutura atual no ConsoleAI',
  },
  // Padrao de ambiente novo (spec 2026-10-08, TASK-24806).
  // Conta PRD do projeto pela organizacao (A2, spec 2026-10-08 §4).
  validate_new_project: {
    label: 'Conferência do projeto novo',
    icon: IconRefresh,
    describe: (i) => (typeof i.project_name === 'string' ? i.project_name : ''),
  },
  create_project_zone: {
    label: 'Zona DNS do projeto',
    icon: IconCloud,
    describe: (i) => (typeof i.project_zone_name === 'string' ? i.project_zone_name : ''),
  },
  create_env_zone: {
    label: 'Zona DNS do ambiente',
    icon: IconCloud,
    describe: (i) => (typeof i.zone_name === 'string' ? i.zone_name : ''),
  },
  seed_bootstrap_secrets: {
    label: 'Segredos de bootstrap',
    icon: IconLock,
    describe: () => 'Falcon, deploy token e chave de clone na conta nova',
  },
  // Repositorio de IaC e token de deploy do projeto novo (consoleai#25771).
  create_project_iac_repo: {
    label: 'Repositório de IaC do projeto',
    icon: IconBuildingFactory2,
    describe: (i) =>
      typeof i.grupo === 'string'
        ? `${i.grupo}/iac/stages e token de deploy do grupo no GitLab`
        : 'Repositório de IaC e token de deploy no GitLab',
  },
  vincular_organizacao: {
    label: 'Vínculo com a organização',
    icon: IconBuildingFactory2,
    describe: () => 'Tenant novo na organização do tenant de origem',
  },
}

const FALLBACK_META: StepMeta = {
  label: 'Step desconhecido',
  icon: IconRefresh,
  describe: () => '',
}

function countTags(tags: unknown): number {
  if (!tags || typeof tags !== 'object') return 0
  return Object.keys(tags as Record<string, unknown>).length
}

export function getStepMeta(stepType: string): StepMeta {
  return STEP_META[stepType] ?? { ...FALLBACK_META, label: humanize(stepType) }
}

function humanize(s: string): string {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function parseStepInput(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>
  return {}
}

export function awsConsoleLink(stepType: string, awsResourceId: string | null | undefined): string | null {
  if (!awsResourceId) return null
  switch (stepType) {
    case 'create_ou':
      return 'https://us-east-1.console.aws.amazon.com/organizations/v2/home/accounts'
    case 'create_account':
    case 'wait_account_active':
    case 'move_account':
    case 'apply_tags_account':
      if (/^\d{12}$/.test(awsResourceId)) {
        return `https://us-east-1.console.aws.amazon.com/organizations/v2/home/accounts/${awsResourceId}`
      }
      return null
    case 'create_iam_user':
    case 'attach_iam_policy':
    case 'create_access_key':
      return 'https://us-east-1.console.aws.amazon.com/iamv2/home#/users'
    case 'store_secret':
    case 'apply_tags_secret':
      return 'https://us-east-1.console.aws.amazon.com/secretsmanager/home?region=us-east-1#!/listSecrets'
    case 'create_email_mailbox':
      return 'https://us-east-1.console.aws.amazon.com/workmail/v2/home'
    default:
      return null
  }
}

export function formatDuration(seconds: number): string {
  if (seconds < 1) return '<1s'
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return s === 0 ? `${m}min` : `${m}min ${s}s`
}

export function formatRelative(secondsAgo: number): string {
  if (secondsAgo < 5) return 'agora'
  if (secondsAgo < 60) return `há ${Math.round(secondsAgo)}s`
  return `há ${Math.floor(secondsAgo / 60)}min`
}

export interface StepGroup {
  id: string
  label: string
  category: StepCategory
  stepIndices: number[]
  /**
   * Endereco derivado que a AWS recusou, quando o provisionamento caiu para
   * `+N` (TASK-10168). Presente so quando houve troca. A tela usa para dizer
   * o que aconteceu em vez de mostrar um endereco que a conta nao tem.
   */
  emailDerivadoRecusado?: string
}

/**
 * Falha terminal de criacao de conta: a AWS ja recusou o `CreateAccount` em
 * definitivo. Repetir a partir deste passo so refaz
 * `describe_create_account_status` do mesmo `request_id`, que nunca muda de
 * estado — no-op garantido. Em 26/08/2026 custou 4 cliques ao operador antes
 * de ele achar o "Re-rodar do inicio". Ver TASK-10150.
 *
 * `TimeoutError` com o pedido ainda IN_PROGRESS e o caso OPOSTO: ali repetir
 * do proprio passo e exatamente o certo.
 */
export function falhaTerminalDeCriacaoDeConta(
  step?: { step_type?: string; status?: string; error_message?: string | null },
): boolean {
  return (
    step?.step_type === 'wait_account_active' &&
    step?.status === 'failed' &&
    (step?.error_message ?? '').includes('create_account FAILED')
  )
}

export function groupSteps(
  steps: {
    step_index: number
    step_type: string
    step_input?: unknown
    step_output?: unknown
  }[],
): StepGroup[] {
  const groups: StepGroup[] = []
  let current: StepGroup | null = null

  const flush = () => {
    if (current && current.stepIndices.length > 0) groups.push(current)
    current = null
  }

  steps.forEach((step) => {
    const input = parseStepInput(step.step_input)

    if (step.step_type === 'force_org_sync') {
      flush()
      current = {
        id: `sync-${step.step_index}`,
        label: 'Finalização',
        category: 'sync',
        stepIndices: [step.step_index],
      }
      flush()
      return
    }

    // Roda DEPOIS do `force_org_sync` (precisa do tenant que o sync cria):
    // pertence a Finalizacao. Sem isto abriria um segundo grupo `setup`.
    if (step.step_type === 'vincular_organizacao') {
      flush()
      const ultimo = groups[groups.length - 1]
      if (ultimo && ultimo.category === 'sync') {
        ultimo.stepIndices.push(step.step_index)
      } else {
        groups.push({
          id: `sync-${step.step_index}`,
          label: 'Finalização',
          category: 'sync',
          stepIndices: [step.step_index],
        })
      }
      return
    }

    if (step.step_type === 'create_email_mailbox') {
      flush()
      const derivado = typeof input.email === 'string' ? input.email : 'ambiente'
      current = {
        id: `env-${step.step_index}`,
        label: `Ambiente · ${derivado}`,
        category: 'environment',
        stepIndices: [step.step_index],
      }
      return
    }

    // O endereco que a conta REALMENTE usa so aparece dois passos adiante, no
    // output do `wait_account_active`. Sem isto o cabecalho afirma o derivado
    // — e desde a TASK-10168 eles podem divergir. Ver TASK-10175.
    if (step.step_type === 'wait_account_active' && current) {
      const output = parseStepInput(step.step_output)
      const usado = output.email_usado
      if (typeof usado === 'string' && usado) {
        const recusados = output.emails_recusados
        const derivado =
          Array.isArray(recusados) && typeof recusados[0] === 'string'
            ? recusados[0]
            : undefined
        current.label = `Ambiente · ${usado}`
        if (derivado && derivado !== usado) {
          current.emailDerivadoRecusado = derivado
        }
      }
    }

    if (!current) {
      current = {
        id: 'setup',
        label: 'Estrutura na AWS Organizations',
        category: 'setup',
        stepIndices: [],
      }
    }
    current.stepIndices.push(step.step_index)
  })

  flush()
  return groups
}
